
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { useMediaQuery, useTheme } from '@mui/material';
import {
    AuthenticatedUserContext,
    Checkbox,
    CollaboratingGroupContext,
    DatePicker,
    DefaultFooterPosition,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    GroupedRatingTable,
    IGroupedRatingTableRowProps,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RadioGroup,
    RouteLoading,
    ToggleButtonGroup,
    UserType
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FunctionComponent, useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';
import SourceDocuments from '../../documents/SourceDocuments';
import TranslatedFooter from './templates/TranslatedFooter';
import { TranslatedProForm } from './TranslatedProForm';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IBreastCancerTreatmentOutcomesScaleFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */



const ratings: IGroupedRatingTableRowProps[] = [
    {
        propertyPath: 'breastSizeScore',
    },
    {
        propertyPath: 'breastTextureScore'
    },
    {
        propertyPath: 'armHeavinessScore'
    },
    {
        propertyPath: 'nippleAppearanceScore'
    },
    {
        propertyPath: 'shoulderMovementScore'
    },
    {
        propertyPath: 'armMovementScore'
    },
    {
        propertyPath: 'breastPainScore'
    },
    {
        propertyPath: 'abilityToLiftObjectsScore'
    },
    {
        propertyPath: 'fitOfShirtSleeveScore'
    },
    {
        propertyPath: 'breastTendernessScore'
    },
    {
        propertyPath: 'shoulderStiffnessScore'
    },
    {
        propertyPath: 'breastShapeScore'
    },
    {
        propertyPath: 'breastElevationScore'
    },
    {
        propertyPath: 'scarTissueScore'
    },
    {
        propertyPath: 'shoulderPainScore'
    },
    {
        propertyPath: 'armPainScore'
    },
    {
        propertyPath: 'armSwellingScore'
    },
    {
        propertyPath: 'breastSwellingScore'
    },
    {
        propertyPath: 'armStiffnessScore'
    },
    {
        propertyPath: 'fitOfBraScore'
    },
    {
        propertyPath: 'breastSensitivityScore'
    },
    {
        propertyPath: 'fitOfClothingScore'
    },
]

const BreastCancerTreatmentOutcomesScaleForm: FunctionComponent<IBreastCancerTreatmentOutcomesScaleFormProps> = () => {
    const theme = useTheme();

    const { data: user } = useContext(AuthenticatedUserContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const isPatient = React.useMemo(() => {
        return user?.type === UserType.Patient;
    }, [user]);

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdatePro, 
            canAdministerPro
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsProUpdate,
            Permission.OpmsProAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const showSteps = isPatient || isMobile;

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <TranslatedProForm
                formTranslationKey='forms.breastCancerTreatmentOutcomesScale'
                validateOn={ValidateOn.onChange}
                useSteps={showSteps}
                readOnly={showSteps ? !canUpdatePro : !canAdministerPro}
                FooterOptions={{
                    content: <TranslatedFooter isPatient={isPatient} />,
                    position: DefaultFooterPosition
                }}
                DialogOptions={AppSettings.proForms.dialogOptions}
                showEventHeading
                debug={AppSettings.debug}
            >
                {
                    !isPatient && (
                        <GroupedFields>
                            <GroupedField
                                propertyPath="dateCompleted"
                                component={DatePicker}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                            <GroupedField
                                LabelProps={{
                                    label: <></>
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.Auto
                                }}
                                propertyPath="ptDidNotComplete"
                                component={Checkbox}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                        </GroupedFields>
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="breastSizeScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="breastSizeScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="breastTextureScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="breastTextureScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="armHeavinessScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="armHeavinessScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="nippleAppearanceScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="nippleAppearanceScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="shoulderMovementScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="shoulderMovementScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="armMovementScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="armMovementScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="breastPainScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="breastPainScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="abilityToLiftObjectsScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="abilityToLiftObjectsScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="fitOfShirtSleeveScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="fitOfShirtSleeveScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="breastTendernessScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="breastTendernessScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="shoulderStiffnessScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="shoulderStiffnessScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="breastShapeScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="breastShapeScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="breastElevationScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="breastElevationScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="scarTissueScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="scarTissueScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="shoulderPainScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="shoulderPainScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="armPainScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="armPainScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="armSwellingScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="armSwellingScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="breastSwellingScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="breastSwellingScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="armStiffnessScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="armStiffnessScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="fitOfBraScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="fitOfBraScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="breastSensitivityScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="breastSensitivityScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="fitOfClothingScore"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="fitOfClothingScore"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        )
                }
                {
                    !showSteps && (
                        <div>    
                            <GroupedRatingTable 
                                rows={ratings}
                                TableProps={{
                                    sx: {
                                        tableLayout: 'fixed'
                                    }
                                }}
                                LabelTableHeadCellProps={{
                                    sx: {
                                        width: '100%'
                                    }
                                }}
                                ValueTableHeadCellProps={{
                                    sx: {
                                        width: 150
                                    }
                                }}
                            />
                        </div>
                    )
                }
                {
                    canAdministerPro && (
                        <SourceDocuments />
                    )
                }
            </TranslatedProForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default BreastCancerTreatmentOutcomesScaleForm;
